import React, { FunctionComponent, Suspense, useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from 'react-oidc-context';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { currencyCode } from '../../../consts';
import { useSnackbar } from '../../../hooks/snackbar';
import { useDeviceInfo } from '../../../hooks/useDeviceInfo';
import { useUserContext } from '../../../providers/UserProvider';
import { selectGameLaunchUrl } from '../../../state/game';
import { setGameData, setGameLaunchTime, setGameLaunchUrl } from '../../../state/game/reducers';
import { getGameAspectRatio } from '../../../utils/games/games.utils';
import { Spinner } from '../../shared/Spinner';
import { GameSearchSpineZone } from '../GameSearch';
import { GameSessionInGame, GameSessionParallel } from '../GameSession';
import { Game, GameChannel, GameType } from '../games.model';
import { retrieveGameLaunchUrl, RetrieveGameLaunchUrlPayload } from '../service';
import { GameLauncherMenu } from './GameLauncherMenu';
import GamingPerformancePrompt from './GamingPerformancePrompt';
import { useGameDetailsUrl, useGameSession } from '../../../hooks/games';
import { useTranslations } from '../../../hooks/useTranslationsHelper';
import './GameLauncher.scss';
import { useSessionContextUpdatedSubscription } from '../../../libs/graphql/baseAppAPI/subscriptions/__generated__/session-context-updated.generated';
import useDestroyUserSession from '../../../hooks/auth/useDestroyUserSession';
import { useBrandedNavigate } from '../../../hooks/useBrandedNavigate';
import { setCashierModal } from '../../../state/cashier/reducers';
import { CashierType } from '../../../state/cashier';
import { trackPlayEvent } from '../../../tracking/tracking';

export type GameContainerProps = {
  game: Game;
  mode: 'demo' | 'real';
  gameType?: GameType;
  isMiniMode?: boolean;
};
/**
 * Component will attempt to launch provided game and display overlay with actions/session info.
 *
 * TODO: What is the unhappy path?  useGameLauncher returns request failure data - use this when unhappy path is defined.
 */
export const GameLauncher: FunctionComponent<GameContainerProps> = ({ game, gameType, isMiniMode = false }) => {
  const { t } = useTranslations();
  const auth = useAuth();
  const { addSnack } = useSnackbar();
  const { isMobileDevice } = useDeviceInfo();
  const { handleGameStartDuringBreak } = useGameSession();
  const gameChannel: GameChannel = isMobileDevice ? GameChannel.Mobile : GameChannel.Desktop;
  const dispatch = useDispatch();
  const gameContainer = useRef<HTMLDivElement>(null);
  const iframeId = 'game-iframe';
  const navigate = useNavigate();
  const { navigateHome } = useBrandedNavigate();
  const { userProfileId } = useUserContext();
  const playerId = userProfileId || (auth.user?.profile?.user_profile_id as string);
  const launchUrl = useSelector(selectGameLaunchUrl);
  const { friendlyUrlName, displayName, brand } = game;
  const { logoutUser } = useDestroyUserSession();
  const { buildGameDetailsUrl } = useGameDetailsUrl(brand, gameType);

  // TODO: add missing logic
  const mocks = {
    gameAspectRatio: getGameAspectRatio(game, gameChannel),
    isLaunchDataInitialised: true,
    isFetching: false,
    gameChannel: 'Desktop',
    isMobileDevice: false,
    gameBackgroundImage: game.gameplayBackground.imgUrl || '',
  };

  const [showPerformancePrompt, setShowPerformancePrompt] = useState(false);
  const [showParallelPrompt, setShowParallelPrompt] = useState(false);
  const [showGameSearch, setShowGameSearch] = useState<boolean>(false);
  const [showMenu, setShowMenu] = useState<boolean>(false);

  const { data } = useSessionContextUpdatedSubscription({
    variables: {
      userProfileId,
    },
  });

  useEffect(() => {
    const additionalProperties = JSON.parse(data?.sessionContextUpdated?.current?.additionalProperties || '{}');
    const isActiveCasinoMessage =
      data?.sessionContextUpdated?.mediatorType === 'casino' &&
      data?.sessionContextUpdated?.current?.status === 'Active';

    if (additionalProperties && isActiveCasinoMessage) {
      // Keep session active when receiving game activity as we can't detect user interactions
      if (auth.user?.expired) {
        const refreshToken = async (): Promise<void> => {
          try {
            await auth.signinSilent();
          } catch (error) {
            console.error('Failed to refresh auth token:', error);
            logoutUser();
          }
        };

        refreshToken();
      }

      if (String(game.gameId) !== String(additionalProperties.Casino.GameExternalId) && gameType === GameType.Game) {
        console.log('new alternate casino session detected, going to home');
        addSnack({ type: 'warning', message: 'Multiple game sessions detected' });
        navigateHome();
      }
    }
  }, [data]);

  useEffect(() => {
    // Set current game data (used to match against session data)
    dispatch(setGameData(game));
    dispatch(setGameLaunchTime());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [friendlyUrlName]);

  const getGameLaunchUrl = useCallback(async (): Promise<void> => {
    try {
      if (auth.user?.expired) {
        await auth.signinSilent();
      }

      const bingoData =
        gameType === GameType.Bingo
          ? ({
              gameType: 'Bingo',
              gamesLobbyUrl: `${window.location.origin}/minigames`,
              depositUrl: 't',
            } as Partial<RetrieveGameLaunchUrlPayload>)
          : {};
      const launchUrl = await retrieveGameLaunchUrl(playerId, String(game.provider?.mediatorId), {
        gameExternalId: game.gameId,
        currencyCode,
        isMiniMode,
        ...bingoData,
      });

      dispatch(setGameLaunchUrl(launchUrl.url));

      try {
        trackPlayEvent(game);
      } catch (trackingError) {
        console.error('Play tracking failed:', trackingError);
      }

      if (gameType === GameType.Game) {
        fetch('/api/recentlyPlayed', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${auth.user?.access_token}`,
          },
          body: JSON.stringify({ userId: playerId, slug: friendlyUrlName }),
        }).catch((error) => {
          console.warn('Recently played update failed:', error);
        });
      }

      return;
    } catch (error) {
      console.error('retrieveGameLaunchUrl error: ', error);
      addSnack({
        type: 'error',
        message: t('errors.general'),
      });
      navigate(buildGameDetailsUrl(friendlyUrlName));
    }
  }, [buildGameDetailsUrl, friendlyUrlName]);

  useEffect(() => {
    window.zE('messenger', 'hide');
    handleGameStartDuringBreak();

    document.body.style.setProperty('overflow', 'hidden');

    const handleBingoMessage = (event: MessageEvent): void => {
      if (gameType === GameType.Bingo && event.data?.name === 'homeButton' && event.data?.sender === 'bingo') {
        navigateHome();
      }

      if (
        gameType === GameType.Bingo &&
        event.data?.name === 'notifyCloseContainer' &&
        event.data?.sender === 'bingo'
      ) {
        navigateHome();
      }

      if (gameType === GameType.Bingo && event.data?.type === 'deposit' && event.data?.sender === 'bingo') {
        dispatch(setCashierModal({ isOpen: true, type: CashierType.Deposit }));
      }
    };

    window.addEventListener('message', handleBingoMessage);

    return () => {
      window.zE('messenger', 'show');
      // setTimeout is used to avoid redefining overflow property in TouchDrawer
      setTimeout(() => {
        document.body.style.setProperty('overflow', null);
      }, 0);
      window.removeEventListener('message', handleBingoMessage);
    };
  }, []);

  useEffect(() => {
    getGameLaunchUrl();
  }, [friendlyUrlName, getGameLaunchUrl]);

  const pragGamesWithScroll = ['vpdt11', 'vpsc10', 'vpmr9', 'vpdr7', 'vplfl6', 'vppso4', 'vpfh3'];

  const scrollAttribute = useCallback((): 'yes' | 'no' => {
    return pragGamesWithScroll.includes(game.gameId) ? 'yes' : 'no';
  }, [game.gameId]);

  return (
    <>
      <Suspense fallback={<Spinner />}>
        <GamingPerformancePrompt
          show={showPerformancePrompt}
          onClose={(accepted): void => {
            if (accepted) {
              console.log('createGameLaunch');
            } else {
              // Byee - casino home
              navigateHome();
            }

            setShowPerformancePrompt(false);
          }}
        />
      </Suspense>

      <GameSessionParallel
        show={showParallelPrompt}
        onClose={(): void => navigate(-1)}
        onPrimary={(): void => {
          setShowParallelPrompt(false);
          console.log('createGameLaunch');
        }}
      />

      <GameSessionInGame />

      <div className={`game-launcher game-launcher--${mocks.gameChannel.toLowerCase()}-channel`}>
        <GameLauncherMenu
          haveLaunched={!!launchUrl}
          iframeId={iframeId}
          onOpenGameSearch={(): void => setShowGameSearch(true)}
          setShowMenu={setShowMenu}
          showMenu={showMenu}
          gameType={gameType}
          isMiniMode={isMiniMode}
        >
          <div ref={gameContainer} className="game-launcher__game-container" style={{ overflow: 'hidden' }}>
            <div
              data-testid="game-container-inner"
              style={mocks?.gameBackgroundImage ? { backgroundImage: `url(${mocks?.gameBackgroundImage})` } : {}}
              className="game-launcher__game-container-inner"
            >
              <div className="game-launcher__game-container-content">
                {Boolean(launchUrl) && (
                  <iframe
                    className="game-launcher__iframe"
                    data-testid={iframeId}
                    id={iframeId}
                    src={launchUrl}
                    title={displayName}
                    allow="xr-spatial-tracking"
                    scrolling={scrollAttribute()}
                  />
                )}
              </div>
            </div>
          </div>
        </GameLauncherMenu>
      </div>
      <GameSearchSpineZone
        isOpen={showGameSearch}
        setShowMenu={setShowMenu}
        onCloseGameSearch={(): void => setShowGameSearch(false)}
      />
    </>
  );
};
